import { TranslocoPipe } from '@jsverse/transloco';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import {
  LucideBarChart3,
  LucideEllipsis,
  LucideHouse,
  LucideTrophy,
  LucideCalendarDays,
} from '@lucide/angular';

/**
 * Mobile-only bottom navigation. Five thumb-zone destinations — Home, Matches,
 * Stats, Community, and a More button that opens the secondary-nav sheet. Hidden at
 * >=1024px where the persistent sidebar takes over (and by the shell during a
 * live match). Home/Matches/Stats light via routerLinkActive; Community spans several
 * URLs so its active state is passed in, as is the More-sheet-open state.
 */
@Component({
  selector: 'app-tab-bar',
  standalone: true,
  imports: [TranslocoPipe, RouterLink, RouterLinkActive, LucideHouse, LucideTrophy, LucideBarChart3, LucideCalendarDays, LucideEllipsis],
  template: `
    <nav class="tab-bar" [attr.aria-label]="'core.primary' | transloco">
      <a class="tab" routerLink="/app/home" routerLinkActive="tab--active" (click)="tabSelect.emit()" [attr.aria-label]="'core.home' | transloco">
        <svg lucideHouse size="22"></svg>
        <span>{{ homeLabel() }}</span>
      </a>
      <a class="tab" routerLink="/app/matches" routerLinkActive="tab--active" (click)="tabSelect.emit()" [attr.aria-label]="'core.matches' | transloco">
        <svg lucideTrophy size="22"></svg>
        <span>{{ matchesLabel() }}</span>
      </a>
      <a class="tab" routerLink="/app/stats" routerLinkActive="tab--active" (click)="tabSelect.emit()" [attr.aria-label]="'core.stats' | transloco">
        <svg lucideBarChart3 size="22"></svg>
        <span>{{ statsLabel() }}</span>
      </a>
      <a class="tab" routerLink="/app/community" [class.tab--active]="communityActive()" (click)="tabSelect.emit()" [attr.aria-label]="'core.community' | transloco">
        <svg lucideCalendarDays size="22"></svg>
        <span>{{ 'core.community' | transloco }}</span>
      </a>
      <button
        class="tab"
        type="button"
        [class.tab--active]="moreActive()"
        (click)="moreClick.emit()"
        [attr.aria-label]="'core.more' | transloco"
        [attr.aria-expanded]="moreActive()"
      >
        <span class="tab__icon-wrap">
          <svg lucideEllipsis size="22"></svg>
          @if (notificationCount() > 0) {
            <span class="tab__badge" aria-hidden="true">{{ notificationCount() > 9 ? '9+' : notificationCount() }}</span>
          }
        </span>
        <span>{{ moreLabel() }}</span>
      </button>
    </nav>
  `,
  styles: [`
    :host { display: none; }

    @media (max-width: 1023px) {
      :host {
        bottom: 0;
        display: block;
        left: 0;
        position: fixed;
        right: 0;
        z-index: 20;
      }
    }

    .tab-bar {
      align-items: stretch;
      background: color-mix(in srgb, var(--color-surface) 88%, var(--color-bg));
      border-top: 1px solid var(--color-border-subtle);
      box-shadow: 0 -6px 20px color-mix(in srgb, var(--color-text-primary) 6%, transparent);
      display: flex;
      padding-bottom: env(safe-area-inset-bottom, 0px);
      backdrop-filter: saturate(1.1) blur(8px);
    }

    .tab {
      align-items: center;
      background: none;
      border: 0;
      color: var(--color-text-secondary);
      cursor: pointer;
      display: flex;
      flex: 1 1 0;
      flex-direction: column;
      font-family: inherit;
      gap: 0.15rem;
      justify-content: center;
      min-height: 3.5rem;
      padding: 0.4rem 0;
      text-decoration: none;
      transition: color var(--motion-fast) var(--ease-standard);
      -webkit-tap-highlight-color: transparent;
    }

    .tab span:last-child {
      font-size: 0.66rem;
      font-weight: 800;
      letter-spacing: 0.01em;
    }

    .tab svg { transition: transform var(--motion-fast) var(--ease-spring); }

    .tab--active { color: var(--color-primary); }
    .tab--active svg { transform: translateY(-1px) scale(1.06); }
    .tab:active svg { transform: scale(0.9); }

    .tab__icon-wrap {
      display: inline-flex;
      position: relative;
    }

    .tab__badge {
      align-items: center;
      background: var(--color-danger);
      border-radius: var(--radius-pill);
      color: var(--color-on-strong);
      display: inline-flex;
      font-size: 0.6rem;
      font-weight: 900;
      justify-content: center;
      line-height: 1;
      min-width: 1rem;
      padding: 0.15rem 0.28rem;
      position: absolute;
      right: -0.55rem;
      top: -0.4rem;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTabBarComponent {
  readonly communityActive = input(false);
  readonly moreActive = input(false);
  readonly notificationCount = input(0);
  readonly homeLabel = input('Home');
  readonly matchesLabel = input('Matches');
  readonly statsLabel = input('Stats');
  readonly moreLabel = input('More');

  readonly moreClick = output<void>();
  readonly tabSelect = output<void>();
}
